import { toNullableNumber } from '../helpers'

/**
 * Maps campaign detail `insertion_order` to the view model.
 * Returns null when the API omits the field.
 * Aligns to the confirmed BFF / finance nest (no legacy flat aliases).
 */

const mapPerson = (
  person?: {
    name?: string | null
    email?: string | null
  } | null
): CampaignLinkedInsertionOrderSeller | null => {
  const name = person?.name?.trim()
  if (!name) return null

  return {
    name,
    email: person?.email?.trim() || null
  }
}

export const mapCampaignLinkedInsertionOrder = (
  raw?: CampaignViewInsertionOrderResponse | null
): CampaignLinkedInsertionOrder | null => {
  const id = raw?.id?.trim()
  const name = raw?.name?.trim()
  if (!id || !name) return null

  return {
    id,
    sequential_id: null,
    name,
    document_status: raw?.document_status ?? null,
    status: raw?.status ?? null,
    currency: raw?.currency || 'BRL',
    total_value: toNullableNumber(raw?.total_value),
    available_budget: toNullableNumber(raw?.available_budget),
    allocated_to_campaign: toNullableNumber(raw?.allocated_to_campaign),
    total_allocated: toNullableNumber(raw?.allocated_budget),
    total_ad_spend: toNullableNumber(raw?.total_ad_spend),
    start_at: raw?.start_date ?? null,
    end_at: raw?.end_date ?? null,
    description: raw?.description ?? null,
    advertiser_group: raw?.organization?.name?.trim() || null,
    agency_name: raw?.agency?.name?.trim() || null,
    responsible_seller: mapPerson(raw?.owners?.sales),
    performance_specialist: mapPerson(raw?.owners?.performance),
    publishers_label: null,
    allowed_ad_formats: raw?.ad_types ?? null,
    campaign_checking_account_id:
      raw?.campaign_checking_account_id?.trim() || null,
    priority: toNullableNumber(raw?.priority)
  }
}

/**
 * Maps campaign detail `insertion_order` when it arrives as a list (current + next PI,
 * ADSP-158 renewal). Reuses the singular mapper per item and drops invalid items.
 * Any non-array input (missing, null, or the old single-object shape) resolves to [].
 */
export const mapCampaignLinkedInsertionOrders = (
  raw?: unknown
): CampaignLinkedInsertionOrder[] => {
  if (!Array.isArray(raw)) return []
  return raw
    .map(item =>
      mapCampaignLinkedInsertionOrder(
        item as CampaignViewInsertionOrderResponse
      )
    )
    .filter((io): io is CampaignLinkedInsertionOrder => io !== null)
}
